import { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Button, Spin } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import db from "../database";
import { getTills, tillRegistration, unlinkTill } from "../services";
import useAuthStore from "../stores/useAuthStore";
import usePOSConfigStore from "../stores/usePOSConfigStore";
import useSessionStore from "../stores/useSessionStore";
import { parseTillAccess } from "../constants/accessControl";
import { getOAuthHeaders } from "../constants/oAuthValidation";
import { timeStamp } from "../utils/timestamp";
import { trace } from "../utils/debugTrace";
import { posTillUser } from "../utils/posSession";
import { isPastBusinessDay } from "../utils/tillBusinessDate";
import {
  classifyTillHold,
  countUnsentOrders,
  describeTillHolder,
  getPosDeviceId,
  heldRegistration,
  isAlreadyLinkedMessage,
  isCloseTillPendingMessage,
  isTillMoveSupported,
  isEstimateOnlyTill,
  raiseTillSequencesFromServer,
  replaceHeldTillHistory,
} from "../utils/tillDeviceRegistration";
// Namespace import: services/index.js re-exports a fixed list without getTillSequenceState.
import * as tillServices from "../services/tillServices";

// BUG-2443 (same till in a second browser profile or on a second device): the first one
// stops once "Use POS Here" on the other moved the till there.
//
// Reads: getTills (one request, this user's tills with their active registration rows)
//   - every CHECK_MS (60 s) while a billing / till screen is open,
//   - when the tab comes back to the front or the window gets focus (at most every 10 s).
// Path watch: a 1 s timer that reads only window.location.pathname (this component
// sits outside the Router).
// While the "Sending saved bills" screen is up only: a SEND_POLL_MS (2 s) timer that
// counts this browser's unsent orders by the isSynced index (countUnsentOrders).
//
// Stops only on "moved" (utils/tillDeviceRegistration classifyTillHold): this browser's
// registration is no longer active AND an active row carries another device's id. Rows
// without a device id (older POS, stray registrations: 66 live tills on 2026-10-09)
// never stop anyone. Restaurant (layout 2) and production tills are not checked.
//
// The POS stays mounted under the overlay, so the sync tick still sends bills already
// saved on this device. Keys are swallowed while the overlay is up, so the keyboard
// shortcuts and the scanner cannot bill.
//
// Close Till on a moved device: the day this device opened can only be closed here (the
// server answers the other one's Open Till with "Close Till is pending"), and only after
// the bills saved here are on the server (isSynced 0 / 2 / 3), so the other one never
// opens on a counter behind them. The cashier is not asked to do that by hand: Close
// Till (the button here, or a previous-day session sent to a Close Till screen) loads
// the billing screen -- the only place that sends bills -- under a full-cover "Sending
// saved bills" screen that cannot be closed or typed through, and opens Close Till when
// the count is 0. tillMovedAway.sendThenClose (useSessionStore, persisted) carries that
// across the page load; PrivateRoute lets a previous-day session reach /pos only while
// it is set. The moved state itself is kept there too, so an offline reload still holds.
export const CHECK_MS = 60000;
export const SEND_POLL_MS = 2000;
const FOCUS_MIN_GAP_MS = 10000;
const PATH_POLL_MS = 1000;
// A Close Till screen reached again this soon after the billing screen was loaded to send
// means the billing screen did not stay open: do not send the browser round again.
const SEND_BOUNCE_MS = 15000;
// The screen whose sync tick sends saved bills (useOrderProcessing syncOrders).
export const SEND_HOME_PATH = "/pos";
export const BILLING_PATHS = ["/pos", "/estimate", "/preorder", "/aggregator", "/call-center", "/open-till", "/open-till-component"];
export const CLOSE_PATHS = ["/close-till", "/close-till-all", "/cash-to-keep"];
export const GUARDED_PATHS = [...BILLING_PATHS, ...CLOSE_PATHS];

export const isGuardedPath = (pathname) => GUARDED_PATHS.includes(pathname);
export const isClosePath = (pathname) => CLOSE_PATHS.includes(pathname);

// The first Close Till screen for this till's template, as the POS menus open it
// (useSuperMarketActions tillUnlink). null when the template has no Close Till (shiftClose N).
export function closeTillPath(posConfig) {
  if (posConfig?.shiftClose !== "Y") return null;
  if (posConfig?.showDenominations === "Y") return "/close-till";
  if (posConfig?.showCloseTillPaymentSummary === "Y") return "/close-till-all";
  return "/cash-to-keep";
}

// Everything the check needs, read at the moment of checking (never captured at mount).
export async function readTillMoveContext() {
  const { tillData, userData } = useAuthStore.getState();
  const { posType, posConfig } = usePOSConfigStore.getState();
  const cwrTill = tillData?.tillAccess?.cwrTill;
  if (!cwrTill?.cwrTillID || !userData?.user_id) return null;
  if (!isTillMoveSupported({ tillAccessController: parseTillAccess(tillData), posType, posConfig })) return null;
  const rows = await db.tillRegistrations.toArray();
  const held = heldRegistration(rows);
  if (!held?.heldId || (held.till && held.till !== cwrTill.till)) return null;
  return { cwrTill, userId: userData.user_id, held, rows };
}

// The stored "moved" answer, only while it belongs to the registration this browser holds now.
export function storedTillMove(ctx) {
  const stored = useSessionStore.getState().tillMovedAway;
  if (!stored || !ctx || stored.heldId !== ctx.held.heldId) return null;
  return stored;
}

const rememberTillMove = (ctx, moved) => {
  const store = useSessionStore.getState();
  const prev = store.tillMovedAway;
  if (moved) {
    // Already stored for this registration: keep it as it is (it may carry sendThenClose).
    if (prev && prev.heldId === ctx.held.heldId) return;
    store.setTillMovedAway({ heldId: ctx.held.heldId, till: ctx.cwrTill.till, at: new Date().toISOString() });
  } else if (prev) {
    store.setTillMovedAway(null);
  }
};

// "Send the saved bills, then open this Close Till screen": set on the stored moved state
// so it survives the page load to the billing screen. false when the till is not moved.
export function beginSendThenClose(closePath) {
  const store = useSessionStore.getState();
  const prev = store.tillMovedAway;
  if (!prev || !closePath) return false;
  store.setTillMovedAway({ ...prev, sendThenClose: closePath, sendStartedAt: Date.now() });
  return true;
}

// Ends it (sent, or the cashier went back). Returns the Close Till screen it was for.
export function endSendThenClose() {
  const store = useSessionStore.getState();
  const prev = store.tillMovedAway;
  if (!prev?.sendThenClose) return null;
  store.setTillMovedAway({ heldId: prev.heldId, till: prev.till, at: prev.at });
  return prev.sendThenClose;
}

// Close Till on this device: { moved, unsent }. moved = the server says another device
// holds the till now, or (server not reachable) the stored answer. unsent is counted
// only when moved.
export async function readMovedCloseState() {
  const ctx = await readTillMoveContext();
  if (!ctx) return { moved: false, unsent: 0 };
  let moved = !!storedTillMove(ctx);
  try {
    const r = await checkTillMoved();
    if (r.state === "moved") moved = true;
    else if (r.state !== "not_checked") moved = false;
    if (r.state !== "not_checked") rememberTillMove(ctx, moved);
  } catch (err) {
    trace("TillMovedGuard", "close check: server not reachable, using stored answer", { moved, message: err?.message });
  }
  const unsent = moved ? await countUnsentOrders(db) : 0;
  return { moved, unsent };
}

// Billing screen on a past-day session (PrivateRoute sends it to Close Till): true only
// while this device's till has moved, the "Sending saved bills" screen was started
// (sendThenClose) and bills saved here are not on the server yet. The billing screen is
// then loaded only to run its sync tick, fully covered by that screen: nobody can bill on
// the previous day. Local reads only: no server call on the route check.
export async function movedTillSendingBills() {
  if (!useSessionStore.getState().tillMovedAway?.sendThenClose) return false;
  const ctx = await readTillMoveContext();
  if (!storedTillMove(ctx)?.sendThenClose) return false;
  return (await countUnsentOrders(db)) > 0;
}

// The open till event is from a previous business day on a template that closes the day
// automatically (the PrivateRoute rule): this device must go to Close Till, not bill.
export async function pastDayCloseDue() {
  try {
    if (usePOSConfigStore.getState().posConfig?.enableAutoStoreClose !== "Y") return false;
    const tillSession = useSessionStore.getState().tillSession;
    if (!tillSession?.tillSessionId || tillSession.tillStatus !== "open") return false;
    const events = await db.tillEvents.where("tillSessionId").equals(tillSession.tillSessionId).toArray();
    const open = events.find((e) => e.tillStatus === "open");
    return !!open && isPastBusinessDay(open);
  } catch (err) {
    trace("TillMovedGuard", "day check failed", { message: err?.message });
    return false;
  }
}

export async function checkTillMoved() {
  const ctx = await readTillMoveContext();
  if (!ctx) return { state: "not_checked" };
  const res = await getTills(ctx.userId);
  const list = res?.data?.data?.getTills;
  if (!Array.isArray(list)) return { state: "not_checked" };
  const till = list.find((t) => t?.cwrTillID === ctx.cwrTill.cwrTillID);
  if (!till) return { state: "not_checked" };
  const hold = classifyTillHold({ activeRows: till.tillHistory, heldId: ctx.held.heldId, deviceId: getPosDeviceId() });
  if (hold.state === "own_row") {
    // This device registered again but the local row was not updated: adopt that row.
    await replaceHeldTillHistory(db, ctx.held.rowId, [{ ...ctx.rows[0].tillHistory[0], ...hold.row }]);
  }
  return { ...hold, till, ctx };
}

// Back office Close Till / Close Store end the day but leave the registration active; only
// back office Unlink Till frees a till another place holds (BUG-2621).
export const UNLINK_STEP_TEXT =
  "If that browser profile or device is no longer used, ask back office to run Unlink Till for this till. Back office Close Till or Close Store does not free the till.";

export const STILL_IN_USE_MESSAGE = (till) =>
  `${till || "This till"} is still linked to another browser profile or device. Do Close Till there (its saved bills are sent before it closes), then press Use POS Here again. ${UNLINK_STEP_TEXT}`;

// "Use POS Here" on the stopped device: hold the till here again once the other place
// has released it. Replaces this browser's registration in place (row [0]). The bill
// counters are raised by the caller (onUseHere). Another place's registration is never
// unlinked from here: it may hold bills saved offline that this device cannot see, and
// the backend refuses the registration while it is active (TILL_ACTIVE_CHECK). Unlinking
// is kept only for an estimate till (no bills, no counter).
export async function takeTillBack() {
  const ctx = await readTillMoveContext();
  if (!ctx) throw new Error("Till details are not available on this device.");
  if (isEstimateOnlyTill(usePOSConfigStore.getState().posConfig)) {
    const unlinked = (await unlinkTill(ctx.cwrTill.cwrTillID))?.data?.data?.unlinkTill;
    if (unlinked?.status !== "200") throw new Error(unlinked?.message || "Could not move the till to this device. Please try again.");
  }
  // Same user value Till.js registers with (the auth cookie's username).
  const username = getOAuthHeaders()?.username || useAuthStore.getState().username;
  const syncId = uuidv4().replace(/-/g, "").toUpperCase();
  const reg = (await tillRegistration(syncId, ctx.cwrTill.cwrTillID, ctx.cwrTill.searchKey, timeStamp(), username))?.data?.data?.tillRegistration;
  if (reg?.status !== "200" || !(reg.tillHistory?.length > 0)) {
    trace("TillMovedGuard", "Use POS Here: till not released by the other place", { status: reg?.status, message: reg?.message });
    throw new Error(isAlreadyLinkedMessage(reg?.message) ? STILL_IN_USE_MESSAGE(ctx.cwrTill.till) : reg?.message || "Could not move the till to this device. Please try again.");
  }
  await replaceHeldTillHistory(db, ctx.held.rowId, reg.tillHistory);
  useSessionStore.getState().setTillMovedAway(null);
  return reg;
}

const tillMoveSupportedNow = () => {
  const { tillData } = useAuthStore.getState();
  const { posType, posConfig } = usePOSConfigStore.getState();
  return isTillMoveSupported({ tillAccessController: parseTillAccess(tillData), posType, posConfig });
};

// Open Till steps (pending notice, counter raise) only for tills that open a day on the
// server; an estimate till (showTillOpening "N") never reaches Open Till.
const tillDayMoveSupportedNow = () => usePOSConfigStore.getState().posConfig?.showTillOpening === "Y" && tillMoveSupportedNow();

// Open Till answered "Close Till is pending": the day is still open on the server, opened on
// another device (or on this one before its data was cleared). Returns the text to show,
// or null to keep today's message (tills that cannot move, any other answer).
// BUG-2582: the text does not say where the day is open; this browser cannot tell another
// device from its own cleared data (OpenTill/closeTillPendingRecovery offers the close here
// when the server says this browser holds the till).
export function closeTillPendingNotice(message) {
  if (!isCloseTillPendingMessage(message) || !tillDayMoveSupportedNow()) return null;
  return {
    title: "Close Till is pending for this till",
    content:
      "This till's day is still open on the server. It was opened in another browser profile or device, or in this browser before its data was cleared. Do Close Till where it was opened (its saved bills are sent before it closes), then open the till here. If that is not possible, ask back office to run Close Till for this till; if the till then shows as linked to another browser profile or device, ask back office to run Unlink Till as well.",
  };
}

// Open Till succeeded: raise the three bill counters to what the server holds now, in case
// they were read before the other device's last bills reached the server. Raise-only,
// never blocks the open; offline it changes nothing.
export async function raiseCountersAfterTillOpen() {
  try {
    if (!tillDayMoveSupportedNow()) return null;
    const cwrTillID = useAuthStore.getState().tillData?.tillAccess?.cwrTill?.cwrTillID;
    const authHeaders = getOAuthHeaders?.() || {};
    const userData = posTillUser(authHeaders);
    if (!cwrTillID || !userData || typeof tillServices.getTillSequenceState !== "function") return null;
    return await raiseTillSequencesFromServer({
      sessionStore: useSessionStore,
      readState: () => tillServices.getTillSequenceState(userData, cwrTillID),
      logEvent: (e) => trace("TillMovedGuard", "counters after Open Till", e),
    });
  } catch (err) {
    trace("TillMovedGuard", "counter refresh after Open Till failed", { message: err?.message });
    return null;
  }
}

// Open Till screen: the Cash to Keep the server holds now, to replace the opening amount
// pre-filled from the value read at sign-in (the other device may have closed the day
// since). null = keep what is shown: template without Retain Cash, a till that cannot
// move, offline, or the same value. One request per Open Till screen.
export async function readServerCashToKeep() {
  try {
    if (usePOSConfigStore.getState().posConfig?.retainCashforNextShift !== "Y" || !tillDayMoveSupportedNow()) return null;
    const cwrTill = useAuthStore.getState().tillData?.tillAccess?.cwrTill;
    const authHeaders = getOAuthHeaders?.() || {};
    const userData = posTillUser(authHeaders);
    if (!cwrTill?.cwrTillID || !userData || typeof tillServices.getTillCashToKeep !== "function") return null;
    const amount = await tillServices.getTillCashToKeep(userData, cwrTill.cwrTillID);
    if (amount == null || amount === Number(cwrTill.cashToKeep || 0)) return null;
    trace("TillMovedGuard", "Open Till: Cash to Keep changed on the server since sign-in", { from: cwrTill.cashToKeep ?? null, to: amount });
    return amount;
  } catch (err) {
    trace("TillMovedGuard", "Cash to Keep read at Open Till failed", { message: err?.message });
    return null;
  }
}

const TillMovedGuard = () => {
  const [path, setPath] = useState(() => window.location.pathname);
  // Starts from the stored answer, so a page load on a moved till is covered from the first
  // paint (the "Sending saved bills" page load, an offline reload). runCheck drops it when
  // it does not belong to the registration this browser holds.
  const [moved, setMoved] = useState(() => {
    const stored = useSessionStore.getState().tillMovedAway;
    return stored ? { lines: [], till: stored.till, fromStore: true } : null;
  });
  const [unsent, setUnsent] = useState(0);
  const [offline, setOffline] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const sendTarget = useSessionStore((s) => s.tillMovedAway?.sendThenClose) || null;
  const lastCheckRef = useRef(0);
  const inFlightRef = useRef(false);
  const guarded = isGuardedPath(path);
  const onClosePath = isClosePath(path);

  // Load the billing screen to send the saved bills, then come back to `closePath`.
  const startSending = useCallback((closePath, count, reason) => {
    if (!beginSendThenClose(closePath)) return false;
    trace("TillMovedGuard", "moved till: sending saved bills before Close Till", { reason, closePath, unsent: count });
    setUnsent(count);
    if (window.location.pathname !== SEND_HOME_PATH) window.location.assign(SEND_HOME_PATH);
    return true;
  }, []);

  const runCheck = useCallback(async (reason) => {
    if (inFlightRef.current || !isGuardedPath(window.location.pathname)) return;
    inFlightRef.current = true;
    lastCheckRef.current = Date.now();
    try {
      const ctx = await readTillMoveContext();
      // Offline reload: start from the stored answer until the server can be asked.
      const stored = storedTillMove(ctx);
      let isMoved = !!stored;
      setMoved((prev) => {
        if (stored) return prev || { lines: [], till: stored.till };
        return prev?.fromStore ? null : prev;
      });
      if (!ctx) return;
      let r = { state: "not_checked" };
      try {
        r = await checkTillMoved();
      } catch (err) {
        // Offline / server error: keep the last answer, try again on the next tick.
        trace("TillMovedGuard", "check failed", { reason, message: err?.message });
      }
      if (r.state === "moved") {
        isMoved = true;
        trace("TillMovedGuard", "till moved to another device", { reason, heldId: r.ctx.held.heldId, holder: r.row });
        setMoved({ lines: describeTillHolder(r.row, r.till), till: r.ctx.cwrTill.till });
      } else if (stored && (r.state === "free" || r.state === "unknown")) {
        // Stopped before, and this browser's registration is still not active: billing
        // here now would be billing with no hold on the till, so another place could
        // take it at any time. Stay stopped until Use POS Here registers it here again.
        isMoved = true;
        trace("TillMovedGuard", "moved till: still not held here", { reason, state: r.state });
        setMoved({
          lines:
            r.state === "free"
              ? ["The other browser profile or device has released this till. Press Use POS Here to bill here again."]
              : describeTillHolder(r.row, r.till),
          till: r.ctx.cwrTill.till,
        });
      } else if (r.state !== "not_checked") {
        isMoved = false;
        const wasMoved = !!stored;
        setMoved((prev) => {
          if (prev) trace("TillMovedGuard", "till held again", { reason, state: r.state });
          return null;
        });
        rememberTillMove(ctx, false);
        // The cover is gone. A previous-day session must not be left on the billing screen:
        // load the page again so the route check sends it to Close Till.
        if (wasMoved && window.location.pathname === SEND_HOME_PATH && (await pastDayCloseDue())) {
          trace("TillMovedGuard", "till held again on a previous-day session → reload for Close Till", { reason });
          window.location.assign(SEND_HOME_PATH);
        }
        return;
      }
      if (r.state !== "not_checked") rememberTillMove(ctx, isMoved);
      if (!isMoved) return;
      const onClose = isClosePath(window.location.pathname);
      const sending = !!useSessionStore.getState().tillMovedAway?.sendThenClose;
      if (!onClose && !sending) return;
      const count = await countUnsentOrders(db);
      setUnsent(count);
      // A Close Till screen with bills still to send (a previous-day session is sent here
      // by the route check): send them first. Not again if the billing screen has just
      // sent the browser back here -- the sending screen then stays up on this screen.
      if (onClose && count > 0) {
        const startedAt = useSessionStore.getState().tillMovedAway?.sendStartedAt || 0;
        if (!sending || Date.now() - startedAt > SEND_BOUNCE_MS) startSending(closeTillPath(usePOSConfigStore.getState().posConfig) || window.location.pathname, count, reason);
        else trace("TillMovedGuard", "billing screen did not stay open to send the saved bills", { reason, unsent: count });
      }
    } catch (err) {
      trace("TillMovedGuard", "check failed", { reason, message: err?.message });
    } finally {
      inFlightRef.current = false;
    }
  }, [startSending]);

  useEffect(() => {
    const id = setInterval(() => {
      const p = window.location.pathname;
      setPath((prev) => (prev === p ? prev : p));
    }, PATH_POLL_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!guarded) return undefined;
    runCheck("screen");
    const id = setInterval(() => runCheck("timer"), CHECK_MS);
    const onFront = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastCheckRef.current < FOCUS_MIN_GAP_MS) return;
      runCheck("focus");
    };
    document.addEventListener("visibilitychange", onFront);
    window.addEventListener("focus", onFront);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onFront);
      window.removeEventListener("focus", onFront);
    };
    // path: check again on every screen change (billing -> Close Till), not only on entry.
  }, [guarded, path, runCheck]);

  // sending: the full-cover screen while this device's saved bills go to the server.
  const sending = !!moved && guarded && !!sendTarget;
  const showMoved = !!moved && guarded && !onClosePath && !sending;
  const show = showMoved || sending;
  const posConfigNow = usePOSConfigStore.getState().posConfig;

  useEffect(() => {
    if (!sending) return undefined;
    let stopped = false;
    const tick = async () => {
      try {
        const count = await countUnsentOrders(db);
        if (stopped) return;
        setUnsent(count);
        setOffline(typeof navigator !== "undefined" && navigator.onLine === false);
        if (count > 0) return;
        const target = endSendThenClose();
        trace("TillMovedGuard", "moved till: saved bills sent → Close Till", { target });
        // Already on a Close Till screen (the route check got there first): stay on it.
        if (target && !isClosePath(window.location.pathname)) window.location.assign(target);
      } catch (err) {
        trace("TillMovedGuard", "unsent count failed", { message: err?.message });
      }
    };
    tick();
    const id = setInterval(tick, SEND_POLL_MS);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [sending]);
  const closePath = closeTillPath(posConfigNow);
  // Estimate till: estimates are saved on the server only, and no day is opened here.
  const estimateOnly = isEstimateOnlyTill(posConfigNow);

  useEffect(() => {
    if (!show) return undefined;
    const swallow = (e) => {
      if (e.target?.closest?.(".till-moved-guard")) return;
      e.stopImmediatePropagation();
      e.preventDefault();
    };
    window.addEventListener("keydown", swallow, true);
    window.addEventListener("keypress", swallow, true);
    window.addEventListener("keyup", swallow, true);
    return () => {
      window.removeEventListener("keydown", swallow, true);
      window.removeEventListener("keypress", swallow, true);
      window.removeEventListener("keyup", swallow, true);
    };
  }, [show]);

  const onUseHere = async () => {
    setBusy(true);
    setError(null);
    try {
      const reg = await takeTillBack();
      // The other one may have billed on this till meanwhile: raise the three counters to
      // what the server holds (raise-only; a no-op for an estimate till and when offline).
      const raised = await raiseCountersAfterTillOpen();
      const closeDue = await pastDayCloseDue();
      const countersRaised = (raised?.changed?.length || 0) > 0;
      trace("TillMovedGuard", "Use POS Here: till taken back", { newId: reg.tillHistory[0].cwrTillRegHistoryId, counters: raised?.changed || [], closeDue });
      if (countersRaised || closeDue) {
        // Load the page again: the billing screen then starts on the raised counters, and
        // the route check sends a previous-day session to Close Till instead of billing.
        // The overlay stays up until the new page is there.
        window.location.assign(window.location.pathname);
        return;
      }
      setMoved(null);
    } catch (err) {
      trace("TillMovedGuard", "Use POS Here failed", { message: err?.message });
      setError(err?.message || "Could not move the till to this device. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // Close Till on the stopped device: straight there when every bill is on the server,
  // else the sending screen first.
  const onCloseTill = async () => {
    let count = 0;
    try {
      count = await countUnsentOrders(db);
    } catch (err) {
      trace("TillMovedGuard", "unsent count failed", { message: err?.message });
    }
    if (count > 0 && startSending(closePath, count, "button")) return;
    window.location.assign(closePath);
  };

  // Back from the sending screen to the stopped message (Use POS Here stays possible).
  const onStopSending = () => {
    endSendThenClose();
    if (window.location.pathname !== SEND_HOME_PATH) window.location.assign(SEND_HOME_PATH);
  };

  if (!show) return null;

  if (sending) {
    const onSendScreen = path === SEND_HOME_PATH;
    return (
      <Modal
        open
        centered
        closable={false}
        footer={null}
        maskClosable={false}
        keyboard={false}
        width={440}
        zIndex={3000}
        wrapClassName="till-moved-guard till-moved-guard--sending"
        styles={{ mask: { backgroundColor: "#F3F4F6" } }}
      >
        <div style={{ textAlign: "center" }}>
          <Spin size="large" style={{ marginBottom: 16 }} />
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{`Sending ${unsent} saved bill${unsent === 1 ? "" : "s"}…`}</div>
          <div style={{ fontSize: 14, color: "#6B7280", marginBottom: 20, lineHeight: 1.6 }}>
            {`${moved.till || "This till"} is now linked to another browser profile or device. Bills saved here are sent to the server before this till is closed. Close Till opens by itself when they are sent. Billing is stopped here.`}
          </div>
          {offline && <div style={{ color: "#D92D20", fontSize: 13, marginBottom: 12 }}>No internet. The bills are sent when the internet is back.</div>}
          {!onSendScreen && (
            <Button type="primary" size="large" block onClick={() => startSending(sendTarget, unsent, "retry")} style={{ height: 44, borderRadius: 8, marginBottom: 8 }}>
              Try Again
            </Button>
          )}
          <Button size="large" block onClick={() => runCheck("button")} style={{ height: 40, borderRadius: 8, marginBottom: 8 }}>
            Check Again
          </Button>
          <Button size="large" block onClick={onStopSending} style={{ height: 40, borderRadius: 8 }}>
            Back
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open centered closable={false} footer={null} maskClosable={false} keyboard={false} width={440} zIndex={3000} wrapClassName="till-moved-guard">
      <div style={{ textAlign: "center" }}>
        <ExclamationCircleFilled style={{ color: "#FFA126", fontSize: 30, marginBottom: 16 }} />
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>This till is now linked to another browser profile or device</div>
        <div style={{ fontSize: 14, color: "#6B7280", marginBottom: 12, lineHeight: 1.6 }}>
          {moved.till ? `${moved.till} is now linked to another browser profile or device.` : "This till is now linked to another browser profile or device."}
          {estimateOnly
            ? " Estimates are stopped here. Estimates already saved are on the server."
            : ` Billing is stopped here. Bills already saved here are sent to the server.${
                closePath
                  ? " To bill here again, do Close Till in the other browser profile or device, then press Use POS Here. To end the day opened here press Close Till."
                  : " To bill here again, do Close Till in the other browser profile or device, then press Use POS Here. If the day opened here must be closed, ask back office."
              } ${UNLINK_STEP_TEXT}`}
        </div>
        <div style={{ fontSize: 13, color: "#374151", marginBottom: 20, lineHeight: 1.6 }}>
          {moved.lines.map((l) => (
            <div key={l}>{l}</div>
          ))}
        </div>
        {error && <div style={{ color: "#D92D20", fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <Button type="primary" size="large" block loading={busy} onClick={onUseHere} style={{ height: 44, borderRadius: 8, marginBottom: 8 }}>
          Use POS Here
        </Button>
        {closePath && (
          <Button size="large" block disabled={busy} onClick={onCloseTill} style={{ height: 40, borderRadius: 8, marginBottom: 8 }}>
            Close Till
          </Button>
        )}
        <Button size="large" block disabled={busy} onClick={() => runCheck("button")} style={{ height: 40, borderRadius: 8 }}>
          Check Again
        </Button>
      </div>
    </Modal>
  );
};

export default TillMovedGuard;
